import React, {Fragment, useCallback} from "react";
import PropTypes from "prop-types";
import {Dialog, Transition} from "@headlessui/react";
import XIcon from "@heroicons/react/solid/XIcon";
import {useTranslation} from "react-i18next";
import {DOCUMENT_EXPORT_OPTIONS_MODAL_Z_INDEX_CLASS} from "../constants";

const DocumentBodyExportOptionsModal = ({
    isOpen,
    onClose,
    onConfirm,
    includeSensitiveData,
    includeProductDataImages,
    onToggleSensitiveData,
    onToggleProductDataImages,
    isLoading,
    errorMessage,
    canToggleSensitiveData,
}) => {
    const {t} = useTranslation();

    const handleRequestClose = useCallback(() => {
        if (isLoading) {
            return;
        }
        onClose();
    }, [isLoading, onClose]);

    const handleConfirm = useCallback(() => {
        if (isLoading) {
            return;
        }
        onConfirm();
    }, [isLoading, onConfirm]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                className={`fixed inset-0 ${DOCUMENT_EXPORT_OPTIONS_MODAL_Z_INDEX_CLASS} overflow-y-auto`}
                onClose={handleRequestClose}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-900 bg-opacity-30 transition-opacity" />
                    </Transition.Child>

                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block w-full max-w-md transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                            <div className="flex items-center justify-between border-b px-4 py-3">
                                <Dialog.Title className="text-lg font-medium leading-6 text-blue-1000">
                                    {t("app.documents.export_title", {defaultValue: "Esporta documento"})}
                                </Dialog.Title>
                                <button
                                    type="button"
                                    onClick={handleRequestClose}
                                    disabled={isLoading}
                                    className="rounded-md p-1 text-blue-1000 hover:bg-blue-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">{t("app.common.close", {defaultValue: "Chiudi"})}</span>
                                    <XIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="px-4 py-5">
                                <p className="text-sm text-gray-600">
                                    {t("app.documents.export_description", {defaultValue: "Seleziona le opzioni di esportazione."})}
                                </p>
                                <div className="mt-4 space-y-3">
                                    {canToggleSensitiveData && (
                                        <label className="flex items-center gap-3 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-gray-300 text-blue-1000 focus:ring-blue-500"
                                                checked={includeSensitiveData}
                                                onChange={(event) => onToggleSensitiveData(event.target.checked)}
                                                disabled={isLoading}
                                            />
                                            <span>{t("app.documents.export_include_sensitive", {defaultValue: "Includi dati sensibili"})}</span>
                                        </label>
                                    )}
                                    <label className="flex items-center gap-3 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-blue-1000 focus:ring-blue-500"
                                            checked={includeProductDataImages}
                                            onChange={(event) => onToggleProductDataImages(event.target.checked)}
                                            disabled={isLoading}
                                        />
                                        <span>{t("app.documents.export_include_images", {defaultValue: "Includi immagini dati prodotto"})}</span>
                                    </label>
                                </div>
                                {errorMessage && (
                                    <p className="mt-4 text-sm text-red-600">
                                        {errorMessage}
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 border-t px-4 py-3">
                                <button
                                    type="button"
                                    onClick={handleRequestClose}
                                    disabled={isLoading}
                                    className="inline-flex h-8 items-center rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {t("app.common.cancel", {defaultValue: "Annulla"})}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={isLoading}
                                    className="inline-flex h-8 items-center rounded-md bg-blue-1000 px-3 text-sm font-semibold text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isLoading
                                        ? t("app.common.loading", {defaultValue: "Caricamento..."})
                                        : t("app.common.export", {defaultValue: "Esporta"})}
                                </button>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

DocumentBodyExportOptionsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    includeSensitiveData: PropTypes.bool.isRequired,
    includeProductDataImages: PropTypes.bool.isRequired,
    onToggleSensitiveData: PropTypes.func.isRequired,
    onToggleProductDataImages: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    errorMessage: PropTypes.string,
    canToggleSensitiveData: PropTypes.bool,
};

DocumentBodyExportOptionsModal.defaultProps = {
    errorMessage: null,
    canToggleSensitiveData: true,
};

export default DocumentBodyExportOptionsModal;
